// eslint-disable-next-line @typescript-eslint/ban-ts-comment -- Icon/Loading are still JS
// @ts-nocheck — remove once Icon and Loading are converted to TypeScript
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { useContext, useState } from "react";
import styled, { ThemeContext, ThemeProvider } from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import shouldForwardProp from "@styled-system/should-forward-prop";
import { compose, layout, position, space } from "styled-system";
import type {
  LayoutProps,
  PositionProps,
  SpaceProps,
  TypographyProps,
} from "styled-system";

import Icon from "../Icon";
import Loading from "../Loading";

export type NotificationIconProps = SpaceProps &
  TypographyProps & {
    /** Font Awesome name, or `[style, name]` pair. */
    icon: string | readonly string[];
    size?: string;
    title?: string;
  };

/**
 * Inline or floating message tied to an event (save, error, progress).
 *
 * Uses a variant tint (`*10` light / `*20` dark) with a matching accent bar
 * and icon. Body text stays on `bodyText` so contrast holds in both themes.
 */

/** Accent. `informational` matches `default`. Not `"danger"`/`"primary"`. */
export type NotificationVariant =
  | "default"
  | "informational"
  | "success"
  | "warning"
  | "error";

const ACCENT_WIDTH = "4px";

// Colour token per theme: the accent sits on a tinted fill, so light mode needs
// the darker amber (base `warning` is ~2.2:1 on white) and dark mode needs the
// lightened hues to clear 3:1 on the near-black surface.
const ACCENT_TOKENS = {
  default: { light: "primary", dark: "primaryLight" },
  informational: { light: "primary", dark: "primaryLight" },
  success: { light: "success", dark: "successLight" },
  warning: { light: "warning", dark: "warningLight" },
  error: { light: "danger", dark: "dangerLight" },
} as const satisfies Record<
  NotificationVariant,
  { light: string; dark: string }
>;

type AccentToken = (typeof ACCENT_TOKENS)[NotificationVariant][
  | "light"
  | "dark"];

const BACKGROUND_TOKENS = {
  default: { light: "primary10", dark: "primary20" },
  informational: { light: "primary10", dark: "primary20" },
  success: { light: "success10", dark: "success20" },
  warning: { light: "warning10", dark: "warning20" },
  error: { light: "danger10", dark: "danger20" },
} as const satisfies Record<
  NotificationVariant,
  { light: string; dark: string }
>;

type BackgroundToken = (typeof BACKGROUND_TOKENS)[NotificationVariant][
  | "light"
  | "dark"];

// Written as `[style, name]` pairs so the icon inventory scanner registers them
// (scripts/fontawesome/extract-used-icon-strings.js only reads the pair form).
const DEFAULT_ICONS = {
  default: ["far", "info-circle"],
  informational: ["far", "info-circle"],
  success: ["far", "check-circle"],
  warning: ["far", "exclamation-circle"],
  error: ["far", "circle-xmark"],
} as const satisfies Record<NotificationVariant, readonly [string, string]>;

const accentColor = (props: { $accent: AccentToken }) =>
  themeGet(`colors.${props.$accent}`)(props);

const backgroundColor = (props: { $bg: BackgroundToken }) =>
  themeGet(`colors.${props.$bg}`)(props);

const NotificationStyles = compose(space, layout, position);

const NotificationWrapper = styled("div").withConfig({
  shouldForwardProp: (prop) =>
    typeof prop === "string" &&
    !prop.startsWith("$") &&
    shouldForwardProp(prop),
})<{ $accent: AccentToken; $bg: BackgroundToken }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: ${themeGet("space.s")};
  /* Clears the accent bar so content keeps even breathing room either side. */
  padding-left: ${themeGet("space.r")};
  border: ${themeGet("borderWidths.1")} solid ${themeGet("colors.dividerLight")};
  border-radius: ${themeGet("radii.2")};
  /* Opaque backing: the *10/*20 tints are rgba, so without this the page shows through. */
  background-color: ${themeGet("colors.surface")};
  background-image: linear-gradient(${backgroundColor}, ${backgroundColor});
  color: ${themeGet("colors.bodyText")};
  font-size: ${themeGet("fontSizes.1")};
  font-weight: ${themeGet("fontWeights.2")};
  cursor: default;

  /* Offset by the border width so the bar reads as flush with the edge. */
  &::before {
    content: "";
    position: absolute;
    top: -${themeGet("borderWidths.1")};
    bottom: -${themeGet("borderWidths.1")};
    left: -${themeGet("borderWidths.1")};
    width: ${ACCENT_WIDTH};
    border-radius: ${themeGet("radii.2")} 0 0 ${themeGet("radii.2")};
    background-color: ${accentColor};
  }

  &.floating {
    position: fixed;
    z-index: ${themeGet("zIndices.13")};
    box-shadow: ${themeGet("shadows.boxDefault")};
  }

  &.centered {
    left: 50%;
    right: auto;
    transform: translateX(-50%);
  }

  ${NotificationStyles}
`;

// A div, not a `p`: call sites nest Flex/Typography blocks in the message, and
// the browser closes a paragraph before block children.
const NotificationContent = styled("div")<{ $closable: boolean }>`
  line-height: ${themeGet("fontSizes.3")};
  padding-right: ${(props) => (props.$closable ? themeGet("space.s")(props) : "0")};
`;

const CloseButton = styled("button")`
  appearance: none;
  margin-left: auto;
  padding: 0 ${themeGet("space.xxs")};
  border: none;
  background: transparent;
  opacity: 0.7;
  border-radius: 10px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: solid 1px transparent;
  transition: ${themeGet("transition.transitionDefault")};

  &:hover,
  &:focus {
    opacity: 1;
    border-color: ${themeGet("colors.dividerDefault")};
  }

  &:focus-visible {
    outline: ${themeGet("borderWidths.2")} solid
      ${themeGet("colors.focusOutline")};
    outline-offset: 1px;
  }
`;

const NotificationIcon = styled(Icon)<{ $accent: AccentToken }>`
  margin-right: ${themeGet("space.s")};
  font-size: ${themeGet("fontSizes.2")};
  color: ${accentColor};
`;

export interface NotificationProps
  extends
    ComponentPropsWithoutRef<"div">,
    SpaceProps,
    LayoutProps,
    PositionProps {
  children?: ReactNode;
  /**
   * Overrides the variant's default glyph. Kebab-case name only, e.g.
   * `icon="lock"` — the regular (`far`) style is baked in.
   */
  icon?: string | undefined;
  /** Drops the leading icon entirely, e.g. for announcement banners. */
  noIcon?: boolean | undefined;
  /** Props forwarded to the leading `Icon` — see the `Icon` component. */
  iconProps?: Partial<NotificationIconProps> | undefined;
  /**
   * Accent: omit/`default`/`informational` (same look), `success`, `warning`,
   * `error`. No `colour` or `altStyle`; `"danger"`/`"primary"` are not valid.
   */
  variant?: NotificationVariant | undefined;
  /** Shows a spinner ahead of the message, e.g. while an import runs. */
  loading?: boolean | undefined;
  /** Floats over content. Combine with `top`/`right`/`bottom`/`left`/`centered`. */
  floating?: boolean | undefined;
  /** Horizontally centres a floating notification; still needs `top`/`bottom`. */
  centered?: boolean | undefined;
  /** Called when the close button is used. Unmount the notification here. */
  onDismiss?: ((dismissed: boolean) => void) | undefined;
  closable?: boolean | undefined;
  /** System design theme to render this notification with. */
  theme?: object | undefined;
}

/** `theme` is `DefaultTheme` (`{}`) to TS, so narrow rather than cast. */
const isDarkTheme = (theme: object | undefined): boolean =>
  !!theme && "isDark" in theme && theme.isDark === true;

const floatingClassName = (floating: boolean, centered: boolean): string => {
  if (!floating) return "";
  return centered ? "floating centered" : "floating";
};

export default function Notification({
  icon,
  noIcon = false,
  variant: variantProp,
  floating = false,
  children,
  centered = false,
  closable = true,
  onDismiss,
  theme,
  iconProps,
  loading = false,
  className,
  ...props
}: NotificationProps) {
  const [dismissed, setDismissed] = useState(false);
  // Read through the `theme` prop first: the wrapper below provides it, so
  // context still holds the ambient theme at this point.
  const contextTheme = useContext(ThemeContext);

  const variant = variantProp ?? "default";

  if (dismissed) {
    return null;
  }

  const onToggle = () => {
    setDismissed(true);
    if (onDismiss) {
      onDismiss(true);
    }
  };

  const themeMode = isDarkTheme(theme ?? contextTheme) ? "dark" : "light";
  const accent = ACCENT_TOKENS[variant][themeMode];
  const bg = BACKGROUND_TOKENS[variant][themeMode];

  // The spinner stands in for the icon, so a loading notification never shows both.
  const leadingIcon =
    noIcon || loading ? null : (icon ?? DEFAULT_ICONS[variant]);

  const component = (
    <NotificationWrapper
      $accent={accent}
      $bg={bg}
      className={[className, floatingClassName(floating, centered)]
        .filter(Boolean)
        .join(" ")}
      role="alert"
      {...props}
    >
      {leadingIcon && (
        <NotificationIcon $accent={accent} icon={leadingIcon} {...iconProps} />
      )}
      {loading && <Loading mr="s" />}
      <NotificationContent $closable={closable}>{children}</NotificationContent>
      {closable && (
        <CloseButton
          className="close-button"
          type="button"
          onClick={onToggle}
          aria-label="Close Notification"
        >
          <Icon icon={["far", "xmark"]} color="bodyText" size="sm" />
        </CloseButton>
      )}
    </NotificationWrapper>
  );

  return theme ? (
    <ThemeProvider theme={theme}>{component}</ThemeProvider>
  ) : (
    component
  );
}
